import { Controller } from 'stimulus'
import $ from 'jquery'

// Cocoon dispara eventos jQuery, invisíveis para addEventListener nativo, e a
// instância que dispara é a global (Sprockets). Mesmo padrão de
// payment_installments_controller.
var $global = typeof window !== 'undefined' && (window.jQuery || window.$) ? window.jQuery || window.$ : $

export default class extends Controller {
  static targets = [
    'toggleInput',
    'kindOptions',
    'configKitLink',
    'compoundHint',
    'compoundColumn',
    'gridDefinitionBtn',
    'generateBtn',
    'skuGrid',
    'skuGridTable',
    'skuGridBtn',
    'removeSkuBtn',
    'automaticStockCompoundInput',
    'configComboBtn',
  ]

  connect() {
    const { observer, config } = this.createSkuGridObserver()
    const form = document.getElementById('new-product-form')
    this.toggleHideNcmAndOrigin()

    if (form) {
      form.addEventListener('keypress', function(e) {
        if (e.key == 'Enter' && !/textarea/i.test((e.target || e.srcElement).tagName)) {
          e.preventDefault()
        }
      })
    }

    if (!this.toggleInputTarget.disabled) {
      this.kindRadios.forEach(radio => (radio.disabled = !this.isActive()))
    }

    observer.observe(this.skuGridTarget, config)
    this.handleCustomVariations(this.isKit())
    this.toggleAdditionalVariation(this.isKit())
    this.toggleDefaultButtons()
    this.toggleAddSkuButton(this.skuGridTarget)
    this.toggleConfigKitBtn()
    this.toggleCompoundHint()
    this.toggleCompoundColumns()
    this.toggleAutomaticStockCompoundInput()

    // Kit/Composto não podem ser fracionados (Product#kit_cant_be_fractionable /
    // #compound_cant_be_fractionable). applyKindChange já cobre isso ao trocar o
    // rádio na criação; faltava rodar no load da edição, onde o rádio já vem
    // travado e change nunca dispara — o checkbox ficava habilitado na tela
    // mesmo sem poder ser marcado de fato.
    this.toggleFractionable(this.isActive())

    // O template que o cocoon usa para "Adicionar SKU" é gerado no render da
    // página, então a linha nova nasce com a visibilidade daquele momento e
    // toggleCompoundColumns (que só roda no connect e na troca do rádio) nunca
    // a alcança. Sem isto: em produto composto criado nesta mesma sessão o
    // botão "Composição" sumiria da linha nova. Namespace no evento para o
    // handler não empilhar a cada navegação do Turbolinks.
    $global(document)
      .off('cocoon:after-insert.productKind')
      .on('cocoon:after-insert.productKind', 'tbody[data-attr="product_skus"]', () => this.toggleCompoundColumns())
  }

  disconnect() {
    $global(document).off('cocoon:after-insert.productKind')
  }

  get kindRadios() {
    return Array.from(document.querySelectorAll('input[type="radio"][name="product[kind]"]'))
  }

  currentKind() {
    const checkedRadio = this.kindRadios.find(radio => radio.checked)

    return checkedRadio ? checkedRadio.value : 'default'
  }

  isActive() {
    return this.toggleInputTarget.checked
  }

  isKit() {
    return this.currentKind() === 'kit'
  }

  handleToggleChange({ target }) {
    const { checked } = target

    this.kindOptionsTarget.hidden = !checked
    this.kindRadios.forEach(radio => (radio.disabled = !checked))

    if (checked) {
      const hasSelection = this.kindRadios.some(radio => radio.checked)
      const [firstRadio] = this.kindRadios

      if (!hasSelection && firstRadio) firstRadio.checked = true
    } else {
      this.kindRadios.forEach(radio => (radio.checked = false))
    }

    this.applyKindChange()
  }

  handleChange() {
    this.applyKindChange()
  }

  applyKindChange() {
    const kind = this.currentKind()
    const active = kind !== 'default'

    this.toggleHideNcmAndOrigin()
    this.toggleSkuGrid()
    this.toggleAdditionalVariation(this.isKit())
    this.toggleKitSkus(kind === 'kit')
    this.toggleFractionable(active)
    this.toggleDefaultButtons()
    this.toggleAddSkuButton()
    this.toggleConfigKitBtn()
    this.toggleCompoundHint()
    this.toggleCompoundColumns()
    this.toggleAutomaticStockCompoundInput()
  }

  // Fora do Kit o botão "Configurar Kit" continua visível, apenas desabilitado
  // (design). Na edição só a opção escolhida é renderizada, então os targets do
  // outro tipo podem não existir.
  toggleConfigKitBtn() {
    if (!this.hasConfigKitLinkTarget) return

    this.configKitLinkTarget.classList.toggle('is-disabled', this.currentKind() !== 'kit')
  }

  toggleCompoundHint() {
    if (!this.hasCompoundHintTarget) return

    this.compoundHintTarget.hidden = this.currentKind() !== 'compound'
  }

  toggleCompoundColumns() {
    const visible = this.currentKind() === 'compound'

    this.compoundColumnTargets.forEach(column => (column.hidden = !visible))
  }

  // O checkbox do Kit (dentro do modal "Configurar Kit") e o hidden do composto
  // compartilham o mesmo name (product[automatic_stock_active]). Um input
  // disabled não é enviado no submit, então mantemos ativo só o par
  // correspondente ao kind atual para não sobrescrever um valor com o outro.
  //
  // O Semantic UI Modal (detachable, comportamento padrão) desloca o modal
  // "Configurar Kit" para fora do escopo deste controller assim que é
  // inicializado, então o campo de lá não pode ser um Stimulus target — vamos
  // direto ao document.
  toggleAutomaticStockCompoundInput() {
    const isCompound = this.currentKind() === 'compound'
    const kitField = document.getElementById('automatic-stock-kit-field')

    if (this.hasAutomaticStockCompoundInputTarget) {
      this.automaticStockCompoundInputTarget.disabled = !isCompound
    }

    if (kitField) kitField.querySelectorAll('input').forEach(input => (input.disabled = isCompound))
  }

  toggleHideNcmAndOrigin() {
    const kit_checked = this.currentKind() === 'kit'
    let ncm_field = document.getElementById('ncm-input-div')
    let origin_field = document.getElementById('origin-input-div')

    ncm_field.hidden = kit_checked
    origin_field.hidden = kit_checked

    if (kit_checked) {
      ncm_field.children[0].classList.remove('required')
      origin_field.children[0].classList.remove('required')
    } else {
      ncm_field.children[0].classList.add('required')
      origin_field.children[0].classList.add('required')
    }
  }

  // Composto passou a exibir os botões de variação: a grade é uma das formas de
  // diferenciar seus vários SKUs, ao lado da ficha técnica, e
  // Product#variants_kit_validation não a proíbe mais ali. Só Kit continua
  // escondendo, porque é limitado a um único SKU
  // (Product#product_sku_unique_for_kit, if: :kit?) — sem um segundo SKU para
  // gerar, a grade não tem o que fazer.
  toggleDefaultButtons() {
    const isKit = this.isKit()

    this.toggleConfigComboBtn()
    this.gridDefinitionBtnTarget.hidden = isKit
    this.gridDefinitionBtnTarget.style.display = isKit ? 'none' : ''

    this.skuGridBtnTargets.forEach(button => (button.hidden = isKit))
  }

  // RN-01: combo só existe em produto `kind` default (a view já exige `@product.default?`).
  // O bloco do combo (checkbox + botão) some com Kit ou Composto marcado, e o checkbox é
  // desmarcado para a composição não ir junto no save.
  toggleConfigComboBtn() {
    if (!this.hasConfigComboBtnTarget) return

    const hidden = this.currentKind() !== 'default'
    const comboInput = this.configComboBtnTarget.querySelector('[data-target="product-combo-toggle.input"]')

    this.configComboBtnTarget.hidden = hidden

    if (hidden && comboInput && comboInput.checked) {
      comboInput.checked = false
      comboInput.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  createSkuGridObserver() {
    return {
      observer: new MutationObserver(this.handleSkuGridMutation),
      config: { attributes: true },
    }
  }

  handleSkuGridMutation = mutations => {
    mutations.forEach(mutation => {
      const { target: skuGrid } = mutation

      this.toggleAddSkuButton(skuGrid)
    })
  }

  toggleAddSkuButton() {
    const checked = this.isActive()
    const tableRows = this.skuGridTableTarget.querySelectorAll('tbody > tr')

    if (!checked && tableRows.length > 0) return

    this.generateBtnTarget.style.display = ''
    this.generateBtnTarget.hidden = false
  }

  toggleAdditionalVariation(checked) {
    const fieldset = document.querySelector('[data-attr="additional_sku_variants_fieldset"]')

    if (!fieldset) return

    fieldset.disabled = checked
    fieldset.hidden = checked
  }

  toggleSkuGrid() {
    this.handleCustomVariations(this.isKit())
    this.handleSkuGridTableRows()
  }

  toggleKitSkus(checked) {
    const destroySkuInputQuery = '[data-attr="kit-item-config-modal"] input[name*=_destroy]'
    const removeSkuInputs = document.querySelectorAll(destroySkuInputQuery)

    removeSkuInputs.forEach(input => {
      input.value = !checked
    })
  }

  toggleFractionable(disableFractionable) {
    const field = document.getElementById('product_fractionable')

    field.disabled = disableFractionable
    field.classList.toggle('disabled', disableFractionable)
    if (disableFractionable) field.checked = false
  }

  handleCustomVariations(checked) {
    const customVariations = this.skuGridTarget.querySelectorAll('[data-attr="variation-custom"]')

    if (customVariations) {
      customVariations.forEach(variation => {
        const inputs = variation.querySelectorAll('select, input')

        variation.hidden = checked

        if (inputs.length > 0) inputs.forEach(input => (input.disabled = checked))
      })
    }
  }

  handleSkuGridTableRows() {
    const removeButtons = this.removeSkuBtnTargets
    const pageAction = document.body.dataset.currentAction

    removeButtons.forEach((button, index) => {
      if (['edit', 'update', 'create'].includes(pageAction) && index === 0) return

      button.click()
    })
  }

  handleSkuCreation() {
    this.toggleAdditionalVariation(this.isKit())
  }
}
